import React, { useState } from "react";
import { FileText, Download, Check, Send, Clock, CircleCheck, CircleDot } from "lucide-react";

export const fmt = (n) => `\u00A3${Number(n || 0).toLocaleString()}`;
export const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—");

export const SectionCard = ({ title, action, children }) => (
  <div className="bg-white rounded-2xl border border-[#f0ece5] p-6">
    {title && (
      <div className="flex items-center justify-between mb-5">
        <h3 className="font-display text-[20px] font-500 text-[#2b2b2b]">{title}</h3>
        {action}
      </div>
    )}
    {children}
  </div>
);

export const StageTimeline = ({ stages = [], current = 0 }) => (
  <div className="flex flex-col sm:flex-row gap-4 sm:gap-0">
    {stages.map((s, i) => {
      const done = i < current;
      const active = i === current;
      return (
        <div key={s} className="flex sm:flex-col items-center sm:flex-1 gap-3 sm:gap-0 relative">
          <div className="sm:mb-2 z-10">
            {done ? <CircleCheck size={26} className="text-[#b8935f] fill-[#b8935f]/10" />
              : active ? <CircleDot size={26} className="text-[#b8935f]" />
              : <div className="w-[26px] h-[26px] rounded-full border-2 border-[#e2d8c8] bg-white" />}
          </div>
          {i < stages.length - 1 && (
            <div className="hidden sm:block absolute top-[13px] left-1/2 w-full h-[2px]" style={{ background: done ? "#b8935f" : "#e2d8c8" }} />
          )}
          <span className={`text-[12px] sm:text-center sm:px-1 ${active ? "text-[#b8935f] font-500" : done ? "text-[#4a4a4a]" : "text-[#a0a0a0]"}`}>{s}</span>
        </div>
      );
    })}
  </div>
);

export const DocumentsList = ({ documents = [] }) => (
  <div className="space-y-2">
    {documents.length === 0 ? <p className="text-[14px] text-[#8a8a8a]">No documents yet.</p> :
      documents.map((d) => (
        <div key={d.id} className="flex items-center justify-between p-3 rounded-lg bg-[#faf7f2] border border-[#f0ece5]">
          <div className="flex items-center gap-3">
            <FileText size={18} className="text-[#b8935f]" />
            <div>
              <p className="text-[14px] text-[#2b2b2b]">{d.name}</p>
              <p className="text-[12px] text-[#a0a0a0]">{d.category} · {fmtDate(d.date)}</p>
            </div>
          </div>
          <Download size={17} className="text-[#8a8a8a] hover:text-[#b8935f] cursor-pointer" />
        </div>
      ))}
  </div>
);

export const InvoicesList = ({ invoices = [] }) => {
  const color = { paid: "bg-green-100 text-green-700", due: "bg-amber-100 text-amber-700", pending: "bg-gray-100 text-gray-600" };
  return (
    <div className="space-y-2">
      {invoices.length === 0 ? <p className="text-[14px] text-[#8a8a8a]">No invoices yet.</p> :
        invoices.map((inv) => (
          <div key={inv.id} className="flex items-center justify-between p-3 rounded-lg bg-[#faf7f2] border border-[#f0ece5]">
            <div>
              <p className="text-[14px] text-[#2b2b2b]">{inv.number} · {fmt(inv.amount)}</p>
              <p className="text-[12px] text-[#a0a0a0]">{inv.description} · {fmtDate(inv.date)}</p>
            </div>
            <span className={`text-[11px] px-2.5 py-1 rounded-full capitalize ${color[inv.status] || color.pending}`}>{inv.status}</span>
          </div>
        ))}
    </div>
  );
};

export const MessagesThread = ({ messages = [], onSend }) => {
  const [text, setText] = useState("");
  const submit = (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    onSend(text.trim());
    setText("");
  };
  return (
    <div className="flex flex-col h-[420px]">
      <div className="flex-1 overflow-y-auto space-y-3 pr-1">
        {messages.map((m) => (
          <div key={m.id} className={`flex ${m.from === "client" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[75%] px-4 py-2.5 rounded-2xl text-[13.5px] leading-relaxed ${m.from === "client" ? "bg-[#b8935f] text-white rounded-br-sm" : "bg-[#faf7f2] text-[#3a3a3a] border border-[#f0ece5] rounded-bl-sm"}`}>
              {m.text}
              <div className={`text-[10px] mt-1 ${m.from === "client" ? "text-white/70" : "text-[#a0a0a0]"}`}>{fmtDate(m.createdAt)}</div>
            </div>
          </div>
        ))}
      </div>
      <form onSubmit={submit} className="flex gap-2 pt-3 border-t border-[#f0ece5] mt-3">
        <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Message your agent..." className="flex-1 h-[46px] px-4 rounded-full border border-[#e2d8c8] text-[14px] outline-none focus:ring-2 focus:ring-[#b8935f]/30 focus:border-[#b8935f]" />
        <button type="submit" className="w-11 h-11 rounded-full bg-[#b8935f] flex items-center justify-center hover:bg-[#a67f4d] transition-colors"><Send size={17} className="text-white" /></button>
      </form>
    </div>
  );
};

export const ComplianceList = ({ items = [] }) => {
  const color = { valid: "bg-green-100 text-green-700", "due-soon": "bg-amber-100 text-amber-700", expired: "bg-red-100 text-red-700" };
  return (
    <div className="space-y-2">
      {items.map((c) => (
        <div key={c.id} className="flex items-center justify-between p-3 rounded-lg bg-[#faf7f2] border border-[#f0ece5]">
          <div>
            <p className="text-[14px] text-[#2b2b2b]">{c.type}</p>
            <p className="text-[12px] text-[#a0a0a0]">{c.property} · expires {fmtDate(c.expires)}</p>
          </div>
          <span className={`text-[11px] px-2.5 py-1 rounded-full capitalize ${color[c.status] || color.valid}`}>{c.status.replace("-", " ")}</span>
        </div>
      ))}
    </div>
  );
};
